/**
 * Filter-as-you-type over `{ id, label }` choices — ONE control, used three
 * times on the assign sheet (operator, driver, vehicle) and nowhere forked.
 *
 * WHY IT REPLACED A `<select>`
 * ----------------------------
 * The office has many operators and every one of their drivers on the same
 * list. On a phone that is a long, slow scroll through a native wheel with
 * no way to jump: a dispatcher who knows they want Kovács has to find Kovács.
 * Typing three letters is the whole of the improvement.
 *
 * WHAT IT MATCHES: what the row SHOWS, nothing else — a driver by name, a
 * vehicle by type or plate, an operator by name — through the portal's one
 * thumb-typed matcher (`looseIncludes`, portalFormat), so case, accents and
 * punctuation are all ignored: `kovacs` finds `Kovács` and `ab123` finds
 * `AB-123`. The board's own search uses the very same function.
 *
 * ON A PHONE
 * ----------
 * 48px input, 44px rows, and a 16px font — anything smaller and iOS zooms
 * the page in the moment the field takes focus. The list scrolls inside
 * itself (`overscroll-behavior: contain`), so reaching its end does not
 * start dragging the sheet underneath. What is chosen is shown in the field,
 * and a × beside it takes it back off.
 *
 * ACCESSIBILITY: it is a combobox, and is built as the pattern says — a text
 * input with `role="combobox"`, `aria-expanded`, `aria-controls` and
 * `aria-activedescendant`; a `role="listbox"` of `role="option"` rows, each
 * with `aria-selected`; ↑/↓ to move (opening the list if it is closed),
 * Enter to take the active row, Escape to close without changing anything,
 * Tab to leave. The active row is never focused — focus stays in the input,
 * which is what keeps the keyboard up on a phone and what
 * `aria-activedescendant` is for.
 *
 * EMPTY IS AN ANSWER, NOT A FAULT. "This operator has no drivers" is a real
 * state of the world — the sheet narrows both lists to the operator on it —
 * so it is said in words, with the field disabled, rather than left as a box
 * that opens on nothing.
 */
import React, {
  useEffect, useId, useMemo, useRef, useState,
} from 'react';
import { CloseIcon } from './Icons';
import { looseIncludes } from '../portalFormat';

/** The chosen row's label, or '' when nothing is chosen (or it has gone). */
const labelOf = (choices, value) => choices.find((choice) => choice.id === value)?.label || '';

export default function ChoicePicker({
  id,
  label,
  value,
  onChange,
  choices,
  disabled = false,
  placeholder = '',
  /** What to say instead of the control when there is nothing to choose from. */
  empty = 'Nothing to choose from.',
  /** 'Clear the driver' — names the × for a screen reader. */
  clearLabel = 'Clear',
  /** '3 drivers match' is overkill; this names the one no-match row. */
  noMatch = 'Nothing matches what you typed.',
}) {
  const listId = useId();
  const optionId = useId();
  const [open, setOpen] = useState(false);
  // What has been typed since the list opened. `null` means "nothing yet —
  // show what is chosen", which is what keeps the field readable when it is
  // closed and stops a chosen label being treated as a filter.
  const [typed, setTyped] = useState(null);
  const [active, setActive] = useState(0);
  const box = useRef(null);
  const list = useRef(null);

  const chosen = labelOf(choices, value);
  const query = typed === null ? '' : typed;
  const shown = useMemo(
    () => choices.filter((choice) => looseIncludes(choice.label, query)),
    [choices, query],
  );

  // The active row follows the list: a filter that leaves three rows must
  // not leave the highlight on the seventh.
  useEffect(() => {
    setActive((was) => (was < shown.length ? was : 0));
  }, [shown.length]);

  // Keep the active row in view while arrowing through a long list. It is
  // never focused — focus stays in the input — so nothing else scrolls it.
  useEffect(() => {
    if (!open || !list.current) return;
    const row = list.current.children[active];
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const nothingToChoose = !choices.length;

  const close = () => {
    setOpen(false);
    setTyped(null);
  };

  const pick = (choice) => {
    onChange(choice.id);
    close();
  };

  const clear = () => {
    onChange('');
    setTyped(null);
    setOpen(false);
    if (box.current) box.current.focus();
  };

  const move = (step) => {
    setOpen(true);
    if (!shown.length) return;
    setActive((was) => (was + step + shown.length) % shown.length);
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      move(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      move(-1);
      return;
    }

    if (event.key === 'Enter') {
      // Only when it would do something: an Enter on a closed picker is the
      // dispatcher submitting the form, and must stay that.
      if (!open || !shown[active]) return;
      event.preventDefault();
      pick(shown[active]);
      return;
    }

    // Escape closes this list and NOT the sheet around it, which is what the
    // same key does when nothing is open here.
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      close();
      return;
    }

    if (event.key === 'Tab') close();
  };

  if (nothingToChoose) {
    return (
      <>
        <span className="pp-op-form__label">{label}</span>
        <p className="pp-op-form__hint" role="status">{empty}</p>
      </>
    );
  }

  return (
    <>
      <label className="pp-op-form__label" htmlFor={id}>{label}</label>
      <div className={`pp-pick${open ? ' is-open' : ''}`}>
        <input
          id={id}
          ref={box}
          className="pp-pick__input"
          type="text"
          role="combobox"
          autoComplete="off"
          // A phone keyboard with no autocorrect: these are names and plates.
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck="false"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && shown[active] ? `${optionId}-${active}` : undefined}
          value={typed === null ? chosen : typed}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(event) => {
            setTyped(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          // A tap on a row fires mousedown before blur, and the row's own
          // handler prevents the blur; anything else really is a way out.
          onBlur={close}
          onKeyDown={onKeyDown}
        />
        {value && !disabled ? (
          <button
            type="button"
            className="pp-pick__clear"
            aria-label={clearLabel}
            onMouseDown={(event) => event.preventDefault()}
            onClick={clear}
          >
            <CloseIcon size={18} />
          </button>
        ) : null}
        <ul
          className="pp-pick__list"
          id={listId}
          ref={list}
          role="listbox"
          aria-label={label}
          hidden={!open || !shown.length}
        >
          {shown.map((choice, index) => (
            /*
              No key handler on the row, on purpose, and no tabindex: in a
              combobox the OPTIONS are never focused — focus stays in the
              input and `aria-activedescendant` points at the active row,
              which is what keeps an iPhone's keyboard up while arrowing
              through the list. Every key this control answers is handled on
              the input above (↑ ↓ Enter Escape Tab).
            */
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <li
              key={choice.id}
              id={`${optionId}-${index}`}
              className={`pp-pick__option${index === active ? ' is-active' : ''}`}
              role="option"
              aria-selected={choice.id === value}
              // Keeps the field focused, so the pick lands before the blur.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(choice)}
            >
              {choice.label}
            </li>
          ))}
        </ul>
        {/* Outside the listbox on purpose: a listbox's children are options,
            and this is an answer, not something to choose. It is a live
            region, so it is heard as well as seen. */}
        <p className="pp-pick__none" role="status" hidden={!open || Boolean(shown.length)}>
          {noMatch}
        </p>
      </div>
    </>
  );
}
